import { useState } from 'react';
import { GOLF_GAMES, GAMES_CATEGORIES } from '@/lib/reference/golf-games';
import type { GameCategory, GolfGameFormat } from '@/lib/reference/golf-games';
import type { LiveEngineId } from '@/lib/games/live-engine-shared';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  onClose: () => void;
  onOpenEngine?: (id: LiveEngineId) => void;
}

const CAT_STYLES: Record<GameCategory, string> = {
  Format: 'bg-navy/10 text-navy',
  Wager: 'bg-gold/20 text-gold-deep',
  Team: 'bg-good/15 text-good',
  'Side Bet': 'bg-bad/10 text-bad',
};

export function GamesLibrarySheet({ onClose, onOpenEngine }: Props) {
  const [cat, setCat] = useState<GameCategory | 'all'>('all');
  const [gameId, setGameId] = useState<string | null>(null);

  const filtered = cat === 'all' ? GOLF_GAMES : GOLF_GAMES.filter((g) => g.cat === cat);
  const game = gameId ? GOLF_GAMES.find((g) => g.id === gameId) : null;

  if (game) {
    return (
      <Sheet title={game.name} onClose={() => setGameId(null)}>
        <div className="space-y-4 px-4 pb-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${CAT_STYLES[game.cat]}`}>{game.cat}</span>
            <span className="text-xs text-mute">{game.players} · Complexity {game.complexity}/5</span>
          </div>
          <p className="text-sm leading-relaxed text-ink-soft">{game.summary}</p>
          <div className="rounded-xl border border-line bg-cream p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-navy">Rules</div>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">{game.rules}</p>
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet title="Games Library" onClose={onClose}>
      <div className="px-4 pb-8">
        <p className="mb-4 text-xs leading-relaxed text-mute">
          Every gambling format and side game you might play out here, with the rules explained. Tap one for the full breakdown.
        </p>
        <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
          {GAMES_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold ${cat === c ? 'bg-navy text-cream shadow-bt' : 'bt-flat border border-line bg-paper text-ink-soft'}`}
            >
              {c === 'all' ? `All (${GOLF_GAMES.length})` : c}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {filtered.map((g) => (
            <GameListItem key={g.id} game={g} onSelect={() => setGameId(g.id)} />
          ))}
        </div>
      </div>
    </Sheet>
  );
}

function GameListItem({ game, onSelect }: { game: GolfGameFormat; onSelect: () => void }) {
  return (
    <button type="button" onClick={onSelect} className="flex w-full items-start gap-3 rounded-[14px] bg-paper p-3.5 text-left shadow-bt">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-serif text-[15px] font-bold text-ink">{game.name}</div>
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${CAT_STYLES[game.cat]}`}>{game.cat}</span>
        </div>
        <div className="mt-1 text-xs text-mute">{game.players}</div>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-soft">{game.summary}</p>
      </div>
    </button>
  );
}
